function overlap(a, b) {
    const out = new Set();
    for (const fa of a) {
        for (const fb of b) {
            if (fa === fb)
                out.add(fa);
            else if (fb.endsWith('/') && fa.startsWith(fb))
                out.add(fa);
            else if (fa.endsWith('/') && fb.startsWith(fa))
                out.add(fb);
        }
    }
    return [...out];
}
/** Kahn's algorithm over index adjacency; level = longest path from a root. */
function levelize(deps) {
    const n = deps.length;
    const out = Array.from({ length: n }, () => []);
    const indeg = new Array(n).fill(0);
    deps.forEach((ds, i) => {
        for (const d of ds) {
            out[d].push(i);
            indeg[i]++;
        }
    });
    const level = new Array(n).fill(0);
    const order = [];
    const queue = [];
    for (let i = 0; i < n; i++)
        if (indeg[i] === 0)
            queue.push(i);
    for (let h = 0; h < queue.length; h++) {
        const u = queue[h];
        order.push(u);
        for (const v of out[u]) {
            level[v] = Math.max(level[v], level[u] + 1);
            if (--indeg[v] === 0)
                queue.push(v);
        }
    }
    if (order.length === n)
        return { ok: true, level, order };
    const done = new Set(order);
    return { ok: false, remaining: [...Array(n).keys()].filter((i) => !done.has(i)) };
}
function findCycle(deps, remaining) {
    const rem = new Set(remaining);
    // Every remaining node has a remaining dependency; walk until a node repeats.
    const seen = new Map();
    const path = [];
    let cur = remaining[0];
    while (!seen.has(cur)) {
        seen.set(cur, path.length);
        path.push(cur);
        cur = deps[cur].find((d) => rem.has(d));
    }
    return path.slice(seen.get(cur)).sort((x, y) => x - y);
}
export function buildPlanGraph(nodes, opts = {}) {
    const max = opts.max ?? 100;
    if (nodes.length > max)
        return { ok: false, error: { kind: 'too-many', count: nodes.length } };
    const index = new Map();
    for (let i = 0; i < nodes.length; i++) {
        const ref = nodes[i].ref;
        if (index.has(ref))
            return { ok: false, error: { kind: 'duplicate-ref', ref } };
        index.set(ref, i);
    }
    const deps = [];
    const external = new Set();
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const mine = new Set();
        for (const d of node.dependsOn) {
            const j = index.get(d);
            if (j === undefined)
                external.add(d);
            else if (j === i)
                return { ok: false, error: { kind: 'self-dependency', ref: node.ref } };
            else
                mine.add(j);
        }
        deps.push([...mine]);
    }
    const conflictMap = new Map();
    let lv = levelize(deps);
    for (;;) {
        if (!lv.ok) {
            const refs = findCycle(deps, lv.remaining).map((i) => nodes[i].ref);
            return { ok: false, error: { kind: 'cycle', refs } };
        }
        const waveOf = new Map();
        for (let i = 0; i < nodes.length; i++) {
            const l = lv.level[i];
            const w = waveOf.get(l);
            if (w)
                w.push(i);
            else
                waveOf.set(l, [i]);
        }
        let added = false;
        for (const wave of waveOf.values()) {
            for (let x = 0; x < wave.length; x++) {
                const fa = nodes[wave[x]].files;
                if (!fa?.length)
                    continue;
                for (let y = x + 1; y < wave.length; y++) {
                    const fb = nodes[wave[y]].files;
                    if (!fb?.length)
                        continue;
                    const shared = overlap(fa, fb);
                    if (!shared.length)
                        continue;
                    const key = `${wave[x]}:${wave[y]}`;
                    const entry = conflictMap.get(key) ?? {
                        a: wave[x],
                        b: wave[y],
                        files: new Set(),
                    };
                    for (const f of shared)
                        entry.files.add(f);
                    conflictMap.set(key, entry);
                    if (opts.serializeConflicts) {
                        deps[wave[y]].push(wave[x]);
                        added = true;
                    }
                }
            }
        }
        if (!added)
            break;
        lv = levelize(deps);
    }
    if (!lv.ok)
        throw new Error('unreachable');
    const { level, order } = lv;
    const waves = [];
    for (let i = 0; i < nodes.length; i++) {
        const l = level[i];
        waves[l] ??= [];
        waves[l].push(nodes[i].ref);
    }
    // Critical path: longest chain by task count over the (possibly serialized) edges.
    const len = new Array(nodes.length).fill(1);
    const pred = new Array(nodes.length).fill(-1);
    for (const u of order) {
        for (const d of [...deps[u]].sort((p, q) => p - q)) {
            if (len[d] + 1 > len[u]) {
                len[u] = len[d] + 1;
                pred[u] = d;
            }
        }
    }
    let end = 0;
    for (let i = 1; i < nodes.length; i++)
        if (len[i] > len[end])
            end = i;
    const criticalPath = [];
    for (let c = nodes.length ? end : -1; c >= 0; c = pred[c])
        criticalPath.unshift(nodes[c].ref);
    const tiers = { fast: 0, standard: 0, deep: 0, unset: 0 };
    for (const n of nodes)
        tiers[n.tier ?? 'unset']++;
    const conflicts = [...conflictMap.values()]
        .sort((p, q) => p.a - q.a || p.b - q.b)
        .map((c) => ({ a: nodes[c.a].ref, b: nodes[c.b].ref, files: [...c.files] }));
    return {
        ok: true,
        graph: {
            waves,
            criticalPath,
            width: waves.reduce((m, w) => Math.max(m, w.length), 0),
            tiers,
            conflicts,
            external: [...external],
        },
    };
}
//# sourceMappingURL=plan-graph.js.map